import { useRef, useState, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Textarea } from '@/shared/components/ui/textarea'
import {
  isChoiceAttributeFieldType,
  isMultiChoiceAttributeFieldType,
  supportsAttributeDefaultValue,
  toSnakeLowercaseKey,
  type AttributeDefaultValue,
  type AttributeOption,
} from './attribute-configuration-utils'

export function AttributeConfigurationField({
  fieldType,
  options,
  onOptionsChange,
  defaultValue,
  onDefaultValueChange,
  showDefaultValue = true,
  readOnly = false,
}: {
  fieldType: string
  options: AttributeOption[]
  onOptionsChange: (options: AttributeOption[]) => void
  defaultValue: AttributeDefaultValue
  onDefaultValueChange: (value: AttributeDefaultValue) => void
  showDefaultValue?: boolean
  /** When true (attribute is bound to a shared attribute template), the
   * options editor renders read-only - type and options are managed by the
   * shared attribute, not this dialog. */
  readOnly?: boolean
}) {
  const { t } = useTranslation()
  const showOptions = isChoiceAttributeFieldType(fieldType)
  const showDefault = showDefaultValue && supportsAttributeDefaultValue(fieldType)
  if (!showOptions && !showDefault) return null

  return (
    <div className="flex flex-col gap-4">
      {showOptions ? (
        <div className="flex flex-col gap-1.5">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
              Options
            </Label>
            <p className="mt-1 text-[10px] text-[var(--ink-3)]">
              {readOnly
                ? t(
                    'templates.attribute.lockedByBucket',
                    'Type and options are managed by the shared attribute',
                  )
                : 'Add the choices shown to users. Option keys are generated as snake_lowercase values.'}
            </p>
          </div>
          <AttributeOptionsInput
            value={options}
            onChange={onOptionsChange}
            readOnly={readOnly}
          />
        </div>
      ) : null}

      {showDefault ? (
        <AttributeDefaultValueInput
          fieldType={fieldType}
          options={options}
          value={defaultValue}
          onChange={onDefaultValueChange}
          readOnly={readOnly}
        />
      ) : null}
    </div>
  )
}

function AttributeDefaultValueInput({
  fieldType,
  options,
  value,
  onChange,
  readOnly = false,
}: {
  fieldType: string
  options: AttributeOption[]
  value: AttributeDefaultValue
  onChange: (value: AttributeDefaultValue) => void
  readOnly?: boolean
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div>
        <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
          Default value
        </Label>
        <p className="mt-1 text-[10px] text-[var(--ink-3)]">
          Optional starting value for this attribute.
        </p>
      </div>
      <DefaultValueControl
        fieldType={fieldType}
        options={options}
        value={value}
        onChange={onChange}
        readOnly={readOnly}
      />
    </div>
  )
}

function DefaultValueControl({
  fieldType,
  options,
  value,
  onChange,
  readOnly = false,
}: {
  fieldType: string
  options: AttributeOption[]
  value: AttributeDefaultValue
  onChange: (value: AttributeDefaultValue) => void
  readOnly?: boolean
}) {
  if (fieldType === 'BOOLEAN') {
    return (
      <Label className="flex cursor-pointer items-center gap-2.5 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2 font-normal">
        <Checkbox
          checked={Boolean(value)}
          onCheckedChange={(checked) => onChange(Boolean(checked))}
          disabled={readOnly}
        />
        <span className="text-[12px] text-[var(--ink-2)]">Default to checked</span>
      </Label>
    )
  }

  if (isMultiChoiceAttributeFieldType(fieldType)) {
    const selected = Array.isArray(value) ? value : []
    return (
      <div className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-2">
        {options.length === 0 ? (
          <p className="text-[11px] text-[var(--ink-3)]">
            Add options before choosing defaults.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {options.map((option) => (
              <Label
                key={option.value}
                className="flex cursor-pointer items-center gap-2 rounded-[4px] px-2 py-1.5 font-normal hover:bg-[var(--bg-subtle)]"
              >
                <Checkbox
                  checked={selected.includes(option.value)}
                  onCheckedChange={(checked) => {
                    const next = checked
                      ? [...selected, option.value]
                      : selected.filter((item) => item !== option.value)
                    onChange(next)
                  }}
                  disabled={readOnly}
                />
                <span className="text-[12px] text-[var(--ink-2)]">
                  {option.label}
                </span>
              </Label>
            ))}
          </div>
        )}
      </div>
    )
  }

  if (fieldType === 'SELECT' || fieldType === 'DOUBLE_SELECT') {
    return (
      <Select
        value={typeof value === 'string' ? value : ''}
        onValueChange={(next) => onChange(next || null)}
        disabled={readOnly}
      >
        <SelectTrigger className="w-full text-[13px]">
          <SelectValue placeholder="No default" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="">No default</SelectItem>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  if (fieldType === 'RICH_TEXT') {
    return (
      <Textarea
        value={typeof value === 'string' ? value : ''}
        onChange={(event) => onChange(event.target.value || null)}
        placeholder="No default"
        className="min-h-[84px]"
        disabled={readOnly}
      />
    )
  }

  const inputType = getInputType(fieldType)
  return (
    <Input
      type={inputType}
      value={value == null || Array.isArray(value) ? '' : String(value)}
      onChange={(event) => {
        const next = event.target.value
        if (fieldType === 'NUMBER' || fieldType === 'MONEY') {
          onChange(next === '' ? null : Number(next))
        } else {
          onChange(next || null)
        }
      }}
      placeholder="No default"
      className="text-[13px]"
      disabled={readOnly}
    />
  )
}

function getInputType(fieldType: string) {
  if (fieldType === 'NUMBER' || fieldType === 'MONEY') return 'number'
  if (fieldType === 'DATE') return 'date'
  if (fieldType === 'DATE_TIME') return 'datetime-local'
  if (fieldType === 'EMAIL') return 'email'
  if (fieldType === 'PHONE') return 'tel'
  if (fieldType === 'LINK') return 'url'
  return 'text'
}

function AttributeOptionsInput({
  value,
  onChange,
  readOnly = false,
}: {
  value: AttributeOption[]
  onChange: (value: AttributeOption[]) => void
  readOnly?: boolean
}) {
  const [draft, setDraft] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  function commitOption(rawValue = draft) {
    const label = rawValue.trim()
    if (!label) {
      setDraft('')
      return
    }

    const option = { value: toSnakeLowercaseKey(label), label }
    if (!option.value || value.some((item) => item.value === option.value)) {
      setDraft('')
      return
    }

    onChange([...value, option])
    setDraft('')
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault()
      commitOption()
    } else if (event.key === 'Backspace' && draft === '' && value.length > 0) {
      onChange(value.slice(0, -1))
    }
  }

  function removeOption(optionValue: string) {
    onChange(value.filter((option) => option.value !== optionValue))
  }

  if (readOnly) {
    return (
      <div>
        <div className="flex min-h-[76px] flex-wrap items-start gap-1.5 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-2">
          {value.length === 0 ? (
            <span className="text-[11px] text-[var(--ink-3)]">No options</span>
          ) : (
            value.map((option) => (
              <Badge key={option.value} variant="secondary" className="h-6 gap-1 text-[11px]">
                {option.label}
              </Badge>
            ))
          )}
        </div>
      </div>
    )
  }

  return (
    <div>
      <div
        className="flex min-h-[76px] cursor-text flex-wrap items-start gap-1.5 rounded-[6px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-2"
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((option) => (
          <Badge key={option.value} variant="secondary" className="h-6 gap-1 text-[11px]">
            {option.label}
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              className="h-4 w-4 rounded-full p-0 text-[var(--ink-3)] hover:text-[var(--ink)]"
              onClick={(event) => {
                event.stopPropagation()
                removeOption(option.value)
              }}
              aria-label={`Remove ${option.label}`}
            >
              <X size={12} />
            </Button>
          </Badge>
        ))}
        <Input
          ref={inputRef}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => commitOption()}
          placeholder={value.length === 0 ? 'Type an option and press Enter' : ''}
          className="h-6 min-w-[160px] flex-1 border-0 bg-transparent px-0 py-0 text-[12px] shadow-none focus-visible:ring-0"
        />
      </div>
      <p className="mt-1 text-[10px] text-[var(--ink-3)]">
        Separate with Enter or comma. Backspace removes the last option.
      </p>
    </div>
  )
}

